/**
 * Invoice PDF Generator — BIG STAR
 *
 * Two layouts:
 * 1. Yard Sale — simple local sale invoice (AED)
 * 2. Overseas/Container — international with shipping details, milestones, terms (USD + customer currency)
 * Also: Proforma, Credit Note, Debit Note variants
 *
 * All PDFs: white background, black text, green accent line, bilingual header
 */

import { DB } from '../data/database';
import { CUT_TYPES } from '../data/constants';
import { resolvePartType } from '../data/partTypes';

// Colors
const GREEN = [16, 185, 129];
const RED = [220, 38, 38];
const GREY_BG = [245, 247, 250];

function getCfg(key) {
  const cfg = DB.get('appConfig');
  if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) return cfg[key] || {};
  return {};
}
const getCompanyInfo = () => {
  const ci = getCfg('company_info');
  return {
    name: ci.name || 'BIG STAR USED CARS & SPARE PARTS TR. L.L.C.',
    name_ar: ci.name_ar || 'بيج ستار للسيارات المستعملة وقطع الغيار ش.ذ.م.م',
    address: ci.address || 'Near Enoc Petrol Pump, Between JNP & Gico signals, Industrial Area No. 3 Sharjah U.A.E',
    office_address: ci.office_address || 'Industrial Area 3 - Industrial - Sharjah - United Arab Emirates',
    po_box: ci.po_box || 'P.O. Box: 35418, Sharjah, UAE',
    po_box_ar: ci.po_box_ar || 'ص.ب : ٣٥٤١٨ الشارقة - إ.ع.م',
    telephone: ci.telephone || '06-5433296',
    fax: ci.fax || '06-5436495',
    mobile1: ci.mobile1 || '050-8583625',
    mobile2: ci.mobile2 || '055-4415563',
    email: ci.email || 'info@bigstaruae.com',
    website: ci.website || '',
    telephone_ar: ci.telephone_ar || '٠٦-٥٤٣٣٢٩٦',
    fax_ar: ci.fax_ar || '٠٦-٥٤٣٦٤٩٥',
    mobile1_ar: ci.mobile1_ar || '٠٥٠-٨٥٨٣٦٢٥',
    mobile2_ar: ci.mobile2_ar || '٠٥٥-٤٤١٥٥٦٣',
    logo_url: ci.logo_url || '',
  };
};
const getBankDetails = () => getCfg('bank_details');
const getTerms = () => {
  const t = getCfg('invoice_terms');
  if (Array.isArray(t)) return t;
  try { return JSON.parse(t); } catch { return []; }
};

// Load logo as base64 for PDF embedding
async function loadLogo(url) {
  if (!url) return null;
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image(); i.crossOrigin = 'anonymous';
      i.onload = () => resolve(i); i.onerror = reject; i.src = url;
    });
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    c.getContext('2d').drawImage(img, 0, 0);
    return { dataUrl: c.toDataURL('image/png'), w: img.width, h: img.height };
  } catch { return null; }
}

// ══════════════════════════════════════════════════════════════
// SHARED: Draw bilingual header
// ══════════════════════════════════════════════════════════════
function drawHeader(doc, co, logo, pageW) {
  const y0 = 8;

  // Center — logo + company name + email only
  if (logo) {
    const ratio = logo.w / logo.h;
    const h = 14; const w = h * ratio;
    doc.addImage(logo.dataUrl, 'PNG', (pageW - w) / 2, y0 - 2, w, h);
  }
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
  doc.text(co.name, pageW / 2, logo ? y0 + 14 : y0 + 4, { align: 'center' });
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
  doc.text(`E-mail: ${co.email}`, pageW / 2, logo ? y0 + 18 : y0 + 8, { align: 'center' });

  // Green line
  const lineY = logo ? y0 + 22 : y0 + 12;
  doc.setDrawColor(...GREEN); doc.setLineWidth(1.5);
  doc.line(14, lineY, pageW - 14, lineY);

  return lineY + 6;
}

// ══════════════════════════════════════════════════════════════
// SHARED: Draw PAID stamp
// ══════════════════════════════════════════════════════════════
function drawPaidStamp(doc, invoice, x, y) {
  if (invoice.status !== 'paid') return;
  const payments = DB.get('payments') || [];
  const lastPaid = payments.filter(p => p.invoiceId === invoice.id && p.status === 'completed').sort((a, b) => (b.verifiedAt || b.paymentDate || '').localeCompare(a.verifiedAt || a.paymentDate || ''))[0];
  const paidDate = lastPaid?.verifiedAt?.split('T')[0] || lastPaid?.paymentDate || invoice.paidDate || '';

  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.7 }));
  doc.setDrawColor(...RED); doc.setTextColor(...RED);
  doc.setLineWidth(2);
  doc.rect(x, y, 50, 28, 'S');
  doc.setFontSize(22); doc.setFont('helvetica', 'bold');
  doc.text('PAID', x + 25, y + 14, { align: 'center' });
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text(paidDate, x + 25, y + 22, { align: 'center' });
  doc.restoreGraphicsState();
  doc.setTextColor(0, 0, 0);
}

// ══════════════════════════════════════════════════════════════
// SHARED: Draw bank details
// ══════════════════════════════════════════════════════════════
function drawBankDetails(doc, bank, y, pageW) {
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
  const fields = [
    ['Title of Account:', bank.title_of_account || ''],
    ['Address:', bank.address || ''],
    ['', bank.phone1 || ''],
    ['IBAN #:', bank.iban || ''],
    [`Account # (${bank.currency || 'AED'}):`, bank.account_number || ''],
    ['Bank & Branch:', bank.bank_and_branch || ''],
    ...(bank.swift_code ? [['Swift Code:', `(${bank.swift_code})`]] : []),
  ].filter(([, v]) => v);
  fields.forEach(([k, v]) => {
    doc.setFont('helvetica', 'bold'); doc.text(k, 14, y);
    doc.setFont('helvetica', 'normal'); doc.text(v, 50, y);
    y += 4;
  });
  doc.setTextColor(0, 0, 0);
  return y;
}

// ══════════════════════════════════════════════════════════════
// SHARED: Footer
// ══════════════════════════════════════════════════════════════
function drawFooter(doc, co, pageW) {
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
    doc.line(14, 280, pageW - 14, 280);
    doc.setFontSize(7); doc.setTextColor(130, 130, 130); doc.setFont('helvetica', 'normal');
    doc.text(co.office_address || co.address, pageW / 2, 284, { align: 'center' });
    doc.text(`Page ${p} of ${pages}`, pageW - 14, 284, { align: 'right' });
  }
}

// ══════════════════════════════════════════════════════════════
// MAIN ENTRY POINT
// ══════════════════════════════════════════════════════════════
export async function generateInvoicePDF(invoice) {
  console.log('[generateInvoicePDF] Starting for:', invoice?.invoiceNumber, 'category:', invoice?.invoiceCategory, 'total:', invoice?.total);
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const doc = new jsPDF();
  const co = getCompanyInfo();
  const bank = getBankDetails();
  const terms = getTerms();
  const logo = await loadLogo(co.logo_url);
  const pageW = doc.internal.pageSize.getWidth();
  const customers = DB.get('customers') || [];
  const customer = customers.find(c => c.id === invoice.customerId);
  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  const containers = DB.get('containers') || [];
  const container = invoice.containerId ? containers.find(c => c.id === invoice.containerId) : null;
  const lineItems = Array.isArray(invoice.lineItems) ? invoice.lineItems : [];
  const milestones = Array.isArray(invoice.paymentMilestones) ? invoice.paymentMilestones : [];
  const isYardSale = invoice.invoiceCategory === 'yard_sale';
  const isProforma = invoice.invoiceCategory === 'proforma';
  const isCreditNote = invoice.invoiceCategory === 'credit_note';
  const isDebitNote = invoice.invoiceCategory === 'debit_note';

  // White background is default in jsPDF

  // ── Header ──
  let y = drawHeader(doc, co, logo, pageW);

  // ── Invoice title + number ──
  doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
  const typeLabel = isCreditNote ? 'CREDIT NOTE' : isDebitNote ? 'DEBIT NOTE' : isProforma ? 'PROFORMA INVOICE' : 'INVOICE';
  doc.text(typeLabel, 14, y);
  doc.setTextColor(...RED); doc.setFontSize(10);
  doc.text('INV NO:', 55, y);
  doc.setTextColor(...GREEN);
  doc.text(invoice.invoiceNumber || '', 78, y);
  doc.setTextColor(0, 0, 0); doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${invoice.issuedDate || new Date().toLocaleDateString('en-GB')}`, pageW - 14, y, { align: 'right' });
  y += 8;

  // ── Customer / Shipping boxes ──
  if (isYardSale || isCreditNote || isDebitNote) {
    // Simple customer row
    doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.5);
    doc.rect(14, y, pageW - 28, 14, 'S');
    doc.setFontSize(7); doc.setTextColor(100, 100, 100);
    doc.text('Customer', 18, y + 3);
    doc.text('Address', 75, y + 3);
    doc.text('Contact', 140, y + 3);
    const pdfCustName = invoice.customerName || customer?.name || '';
    const pdfCustCountry = invoice.customerCountry || customer?.country || 'UAE';
    const pdfCustPhone = invoice.customerPhone || customer?.phone || '';
    doc.setFontSize(9); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold');
    doc.text(pdfCustName, 18, y + 10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${customer?.city || ''}, ${pdfCustCountry}`, 75, y + 10);
    doc.text(pdfCustPhone, 140, y + 10);
    y += 20;
  } else {
    // Bill To + Shipping Details boxes
    const boxW = (pageW - 32) / 2;
    doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.5);
    doc.rect(14, y, boxW, 28, 'S');
    doc.rect(18 + boxW, y, boxW, 28, 'S');
    doc.setFillColor(...GREY_BG);
    doc.rect(14, y, boxW, 6, 'F'); doc.rect(18 + boxW, y, boxW, 6, 'F');
    doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('BILL TO', 18, y + 4);
    doc.text('SHIPPING DETAILS', 22 + boxW, y + 4);
    doc.setFontSize(8); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold');
    doc.text(customer?.company || customer?.name || invoice.customerName || '', 18, y + 11);
    doc.setFont('helvetica', 'normal');
    let by = y + 15;
    if (customer?.name && customer?.company) { doc.text(`Attn: ${customer.name}`, 18, by); by += 4; }
    doc.text(`${customer?.country || ''}`, 18, by); by += 4;
    if (customer?.phone) doc.text(customer.phone, 18, by);

    let sy = y + 11;
    if (container?.number) { doc.text(`Container: ${container.number}`, 22 + boxW, sy); sy += 4; }
    if (container?.vessel) { doc.text(`Vessel: ${container.vessel}${container.voyage ? ' ' + container.voyage : ''}`, 22 + boxW, sy); sy += 4; }
    if (container?.origin) { doc.text(`Origin: ${container.origin}`, 22 + boxW, sy); sy += 4; }
    if (container?.destination) { doc.text(`Destination: ${container.destination}`, 22 + boxW, sy); sy += 4; }
    const dueDate = milestones[0]?.due_date;
    if (dueDate) doc.text(`Due: ${dueDate}`, 22 + boxW, sy);
    y += 34;
  }

  // ── Line Items ──
  const isOverseas = !isYardSale && !isCreditNote && !isDebitNote;
  const cur = invoice.currency || (isYardSale ? 'AED' : 'USD');
  const sym = cur === 'AED' ? 'AED ' : cur === 'JPY' ? '¥' : '$';

  const tableHead = isOverseas
    ? [['No.', 'Article', 'Chassis No', 'HS Code', `Total (${cur})`]]
    : [['No.', 'Article', 'Chassis No', `Amount (${cur})`, `Final (${cur})`]];

  const tableBody = lineItems.map((li, idx) => {
    const unit = allUnits.find(u => u.id === li.unit_id);
    const desc = li.description || '';
    const chassis = unit?.chassis || '—';
    const price = li.fob_price || li.total || 0;

    if (isOverseas) {
      const sku = li.unit_sku || unit?.sku || '';
      const unitForm = resolvePartType(unit);
      const specs = [unitForm, unit?.grade ? `Gr ${unit.grade}` : '', unit?.mileage ? `${Math.round(unit.mileage / 1000)}K km` : ''].filter(Boolean).join(' | ');
      const fullDesc = `${desc}${sku ? `\n${sku}` : ''}${specs ? `\n${specs}` : ''}`;
      const hsCode = unit?.hs_code || (unit?.make ? '8703.24' : unit?.engineName ? '8407.34' : '8708.99');
      return [idx + 1, fullDesc, chassis, hsCode, `${sym}${price.toLocaleString()}`];
    } else {
      return [idx + 1, desc, chassis, `${sym}${price.toLocaleString()}`, `${sym}${price.toLocaleString()}`];
    }
  });

  autoTable(doc, {
    startY: y,
    head: tableHead,
    body: tableBody,
    theme: 'grid',
    headStyles: { fillColor: GREY_BG, textColor: [30, 30, 30], fontSize: 7.5, fontStyle: 'bold', lineColor: [200, 200, 200] },
    bodyStyles: { fontSize: 7.5, cellPadding: 2.5, textColor: [0, 0, 0], lineColor: [220, 220, 220] },
    columnStyles: isOverseas
      ? { 0: { cellWidth: 10 }, 1: { cellWidth: 72 }, 2: { cellWidth: 35 }, 3: { cellWidth: 18 }, 4: { halign: 'right', fontStyle: 'bold' } }
      : { 0: { cellWidth: 10 }, 1: { cellWidth: 70 }, 2: { cellWidth: 38 }, 3: { halign: 'right' }, 4: { halign: 'right', fontStyle: 'bold' } },
    margin: { left: 14, right: 14 },
    styles: { lineColor: [200, 200, 200], lineWidth: 0.3 },
  });

  y = doc.lastAutoTable.finalY + 6;

  // ── Totals ──
  const subtotal = invoice.subtotal || lineItems.reduce((s, li) => s + (li.fob_price || li.total || 0), 0);
  const freight = lineItems.reduce((s, li) => s + (li.freight_share || 0), 0);
  const insurance = invoice.insurance || 0;
  const taxEnabled = invoice.tax_enabled || (invoice.tax || 0) > 0;
  const taxAmt = invoice.tax_amount || invoice.tax || 0;
  const total = invoice.total || subtotal + freight + insurance + taxAmt;

  const totalsX = pageW - 80;
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);

  const addTotalRow = (label, val, bold) => {
    if (bold) { doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0); doc.setFontSize(10); }
    doc.text(label, totalsX, y);
    doc.text(`${sym}${val.toLocaleString()}`, pageW - 14, y, { align: 'right' });
    y += bold ? 6 : 4;
    if (bold) { doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60); doc.setFontSize(8); }
  };

  addTotalRow('Subtotal', subtotal);
  if (freight > 0 && isOverseas) addTotalRow('Freight', freight);
  if (insurance > 0 && isOverseas) addTotalRow('Insurance', insurance);
  if (taxEnabled && taxAmt > 0) addTotalRow(`Tax${invoice.tax_percentage ? ` (${invoice.tax_percentage}%)` : ''}`, taxAmt);
  doc.setDrawColor(180, 180, 180); doc.line(totalsX, y - 1, pageW - 14, y - 1); y += 2;
  addTotalRow(`Total (${cur})`, total, true);

  // Customer currency conversion for overseas
  if (isOverseas && customer?.currency && customer.currency !== cur) {
    const rate = cur === 'USD' && customer.currency === 'AED' ? 3.67 : 1;
    const converted = Math.round(total * rate);
    doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 100, 100);
    doc.text(`Total (${customer.currency}): ${customer.currency} ${converted.toLocaleString()}`, pageW - 14, y, { align: 'right' });
    y += 3;
    doc.text(`Rate: 1 ${cur} = ${rate} ${customer.currency}`, pageW - 14, y, { align: 'right' });
    y += 6;
  }

  // ── Payment Milestones (overseas + non-proforma only) ──
  if (isOverseas && !isProforma && milestones.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    const mw = (pageW - 32) / milestones.length;
    milestones.forEach((m, i) => {
      const mx = 14 + i * (mw + 4);
      doc.setDrawColor(180, 180, 180); doc.rect(mx, y, mw, 22, 'S');
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
      doc.text(`Payment ${i + 1} — ${m.percentage || 50}%`, mx + 3, y + 5);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
      doc.text(m.label || '', mx + 3, y + 9);
      doc.text(`${sym}${(m.amount || 0).toLocaleString()}`, mx + 3, y + 13);
      const isPaid = m.status === 'paid';
      doc.setTextColor(isPaid ? 16 : 200, isPaid ? 185 : 160, isPaid ? 129 : 30);
      doc.text(isPaid ? `PAID — ${m.paid_date || ''} ✓` : 'PENDING', mx + 3, y + 18);
    });
    y += 28;
  }

  // ── Bank Details + PAID stamp ──
  if (y > 230) { doc.addPage(); y = 20; }
  doc.setDrawColor(200, 200, 200); doc.line(14, y, pageW - 14, y); y += 6;
  const bankEndY = drawBankDetails(doc, bank, y, pageW);
  drawPaidStamp(doc, invoice, pageW - 70, y - 2);
  y = bankEndY + 4;

  // ── Terms (overseas only) ──
  if (isOverseas && terms.length > 0 && !isProforma) {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFontSize(6.5); doc.setTextColor(130, 130, 130); doc.setFont('helvetica', 'normal');
    const termsText = terms.map((t, i) => `${i + 1}. ${t}`).join('  ');
    doc.text(termsText, 14, y, { maxWidth: pageW - 28 });
  }

  // ── Footer ──
  drawFooter(doc, co, pageW);

  // ── Save ──
  const custName = (customer?.name || invoice.customerName || '').replace(/[^a-zA-Z0-9]/g, '_');
  doc.save(`${invoice.invoiceNumber || 'Invoice'}_${custName}.pdf`);
}

// ══════════════════════════════════════════════════════════════
// PACKING LIST PDF
// ══════════════════════════════════════════════════════════════
export async function generatePackingListPDF(container, options = {}) {
  const { customerId = null } = options;
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const doc = new jsPDF();
  const co = getCompanyInfo();
  const logo = await loadLogo(co.logo_url);
  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  const customers = DB.get('customers') || [];
  const pageW = doc.internal.pageSize.getWidth();

  let units = (container.units || []).map(uid => allUnits.find(u => u.id === uid)).filter(Boolean);
  if (customerId) units = units.filter(u => u.assignedTo === customerId);

  let y = drawHeader(doc, co, logo, pageW);

  // Title
  doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
  doc.text('PACKING LIST', pageW / 2, y, { align: 'center' }); y += 2;
  doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5);
  doc.line(pageW / 2 - 22, y, pageW / 2 + 22, y); y += 6;

  // Shipper / Date block
  const midX = pageW / 2;
  const rightLblX = midX + 10;
  const rightValX = pageW - 14;
  const depDate = container.actualDeparture || container.plannedDeparture || '';

  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
  doc.text('Shipper:', 14, y);
  doc.text('DATE :', rightLblX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(depDate, rightValX, y, { align: 'right' });
  y += 4;
  doc.text(co.name, 14, y);
  doc.setFont('helvetica', 'bold');
  doc.text('No. :', rightLblX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(container.blNumber || container.number || '', rightValX, y, { align: 'right' });
  y += 4;
  doc.setFontSize(7); doc.setTextColor(60, 60, 60);
  doc.text(co.address, 14, y); y += 7;

  // Consignee / Container info block
  const consignee = customerId ? customers.find(c => c.id === customerId) : null;
  const assignedIds = [...new Set(units.map(u => u.assignedTo).filter(Boolean))];
  const primaryCustomer = consignee || (assignedIds.length === 1 ? customers.find(c => c.id === assignedIds[0]) : null);

  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
  doc.text('Consignee:', 14, y);
  doc.text('CONTAINER NO. :', rightLblX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(container.number || '', rightValX, y, { align: 'right' });
  y += 4;
  if (primaryCustomer) {
    const custLine1 = [primaryCustomer.company, primaryCustomer.name].filter(Boolean).join(' — ');
    doc.text(custLine1, 14, y);
  }
  doc.setFont('helvetica', 'bold');
  doc.text('SEAL NO. :', rightLblX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(container.sealNumber || '', rightValX, y, { align: 'right' });
  y += 4;
  if (primaryCustomer) {
    const custLine2 = [primaryCustomer.city, primaryCustomer.country].filter(Boolean).join(', ');
    if (custLine2) { doc.setTextColor(60, 60, 60); doc.text(custLine2, 14, y); doc.setTextColor(0, 0, 0); }
    y += 4;
    if (primaryCustomer.phone) { doc.setTextColor(60, 60, 60); doc.text(primaryCustomer.phone, 14, y); doc.setTextColor(0, 0, 0); y += 4; }
  }

  // Vessel + route line
  const routeParts = [
    container.vessel ? `Vessel: ${container.vessel}${container.voyage ? ' ' + container.voyage : ''}` : '',
    container.origin ? `Origin: ${container.origin}` : '',
    container.destination ? `Dest: ${container.destination}` : '',
    `Units: ${units.length}`,
  ].filter(Boolean);
  doc.setFontSize(7); doc.setTextColor(80, 80, 80);
  doc.text(routeParts.join('   |   '), 14, y);
  doc.setTextColor(0, 0, 0);
  y += 5;

  // Separator line
  doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
  doc.line(14, y, pageW - 14, y); y += 4;

  // Table
  const body = units.map((u, i) => {
    const make = u.make || '';
    const model = u.model || u.engineName || u.partName || '';
    const details = resolvePartType(u);
    const chassis = u.chassis || '—';
    const engine = u.engineCode || u.engine || '';
    const qty = u.quantity || 1;
    const uom = u.uom || 'PCS';
    const weight = u.weight ? `${u.weight}` : '';
    return [make, model, details, chassis, engine, qty, uom, weight];
  });

  autoTable(doc, {
    startY: y,
    head: [['MAKE', 'Model', 'Details', 'Chassis No.', 'Engine No.', 'Qty', 'UOM', 'Weight\n(KGs)']],
    body,
    theme: 'grid',
    headStyles: { fillColor: GREY_BG, textColor: [30, 30, 30], fontSize: 7.5, fontStyle: 'bold', lineColor: [200, 200, 200], halign: 'center' },
    bodyStyles: { fontSize: 7, cellPadding: 2, textColor: [0, 0, 0], lineColor: [220, 220, 220] },
    columnStyles: { 3: { cellWidth: 32 }, 5: { cellWidth: 10, halign: 'center' }, 6: { cellWidth: 12, halign: 'center' }, 7: { cellWidth: 16, halign: 'center' } },
    margin: { left: 14, right: 14 },
    styles: { lineColor: [200, 200, 200], lineWidth: 0.3 },
  });

  drawFooter(doc, co, pageW);

  const custName = customerId ? (customers.find(c => c.id === customerId)?.name || '').replace(/[^a-zA-Z0-9]/g, '_') : '';
  doc.save(`PackingList_${container.number || container.id}${custName ? `_${custName}` : ''}.pdf`);
}

// ══════════════════════════════════════════════════════════════
// CUSTOMER STOCK PDF — full catalog with images
// ══════════════════════════════════════════════════════════════

async function loadImage(url) {
  if (!url) return null;
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image(); i.crossOrigin = 'anonymous';
      i.onload = () => resolve(i); i.onerror = () => resolve(null); i.src = url;
      setTimeout(() => resolve(null), 5000); // 5s timeout
    });
    if (!img) return null;
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    c.getContext('2d').drawImage(img, 0, 0);
    return c.toDataURL('image/jpeg', 0.7);
  } catch { return null; }
}

export async function generateCustomerStockPDF(customerId) {
  const { jsPDF } = await import('jspdf');
  await import('jspdf-autotable');
  const doc = new jsPDF();
  const co = getCompanyInfo();
  const logo = await loadLogo(co.logo_url);
  const pageW = doc.internal.pageSize.getWidth();
  const customers = DB.get('customers') || [];
  const customer = customers.find(c => c.id === customerId);
  if (!customer) return;

  const allVehicles = DB.get('vehicles') || [];
  const allEngines = DB.get('engines') || [];
  const allParts = DB.get('spareParts') || [];
  const stockEntries = (DB.get('customerStock') || []).filter(s => s.customerId === customerId);
  const quotes = DB.get('priceQuotes') || [];

  // Build unit list from stock entries
  const units = stockEntries.map(s => {
    const v = allVehicles.find(u => u.id === s.unitId);
    if (v) return { ...v, _type: 'vehicle', _addedAt: s.addedAt };
    const e = allEngines.find(u => u.id === s.unitId);
    if (e) return { ...e, _type: 'engine', _addedAt: s.addedAt };
    const p = allParts.find(u => u.id === s.unitId);
    if (p) return { ...p, _type: 'part', _addedAt: s.addedAt };
    return null;
  }).filter(Boolean);

  // Get customer FOB from approved quotes
  const getFob = (unitId) => {
    for (const q of quotes) {
      if ((q.customerId === customerId || q.requestedBy === customerId) && ['approved', 'partially_approved'].includes(q.status) && Array.isArray(q.units)) {
        const m = q.units.find(u => u.unit_id === unitId && u.admin_approved);
        if (m && (m.fob_price || m.pricing?.fob_price)) return { price: m.fob_price || m.pricing.fob_price, date: q.approved_at };
      }
    }
    return null;
  };

  // ── Page 1: Header + customer info ──
  let y = drawHeader(doc, co, logo, pageW);

  doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
  doc.text('CUSTOMER STOCK LIST', 14, y); y += 8;

  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
  const infoLines = [
    `Customer: ${customer.company || customer.name}`,
    customer.company && customer.name !== customer.company ? `Contact: ${customer.name}` : '',
    `Phone: ${customer.phone || '—'}`,
    customer.email ? `Email: ${customer.email}` : '',
    `Country: ${customer.country || '—'}`,
    `Generated: ${new Date().toLocaleDateString('en-GB')}`,
    `Total units in stock: ${units.length}`,
  ].filter(Boolean);
  infoLines.forEach(line => { doc.text(line, 14, y); y += 4; });
  y += 4;

  // ── Units ──
  for (let idx = 0; idx < units.length; idx++) {
    const unit = units[idx];
    const name = unit.make ? `${unit.year ? unit.year + ' ' : ''}${unit.make} ${unit.model || ''}`.trim() : unit.engineName || unit.partName || 'Unit';
    const fobData = getFob(unit.id);
    const images = (unit.images || []).slice(0, 8);

    // Check if we need a new page (need ~80mm minimum for a unit block)
    if (y > 180) { doc.addPage(); y = 20; }

    // Separator
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
    doc.line(14, y, pageW - 14, y); y += 6;

    doc.setFontSize(8); doc.setTextColor(130, 130, 130);
    doc.text(`UNIT ${idx + 1} OF ${units.length}`, 14, y); y += 5;

    // Main image on left
    const imgX = 14; const imgY = y; const imgW = 55; const imgH = 40;
    let mainImg = null;
    if (images[0]) mainImg = await loadImage(images[0]);
    if (mainImg) {
      doc.addImage(mainImg, 'JPEG', imgX, imgY, imgW, imgH);
    } else {
      doc.setFillColor(240, 240, 240);
      doc.rect(imgX, imgY, imgW, imgH, 'F');
      doc.setFontSize(8); doc.setTextColor(180, 180, 180);
      doc.text('No photo', imgX + imgW / 2, imgY + imgH / 2, { align: 'center' });
    }

    // Details on right
    const detX = imgX + imgW + 8;
    let dy = imgY;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text(name, detX, dy + 4); dy += 7;

    doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
    const cutLabel = resolvePartType(unit);
    const specs = [
      unit.sku ? `SKU: ${unit.sku}` : '',
      `Type: ${cutLabel}`,
      unit.chassis ? `Chassis: ${unit.chassis}` : '',
      (unit.grade || unit.score) ? `Grade: ${unit.grade || unit.score}` : '',
      unit.mileage ? `Mileage: ${Number(unit.mileage).toLocaleString()} km` : '',
      unit.transmission ? `Trans: ${unit.transmission}` : '',
      unit.driveSystem ? `Drive: ${unit.driveSystem}` : '',
      unit.steer ? `Steering: ${unit.steer}` : '',
      unit.color ? `Color: ${unit.color}` : '',
      unit.condition ? `Condition: ${unit.condition}` : '',
      unit.country ? `Country: ${unit.country}` : '',
      unit.yard_location ? `Yard: ${unit.yard_location === 'sharjah_yard' ? 'Sharjah' : 'Japan'}` : '',
      `Status: ${(unit.status || 'available').replace(/_/g, ' ')}`,
      fobData ? `FOB: $${fobData.price.toLocaleString()} (quoted ${fobData.date?.split('T')[0] || ''})` : 'FOB: Not priced yet',
    ].filter(Boolean);

    specs.forEach(s => {
      if (dy - imgY > imgH + 2) return; // don't overflow past image area
      doc.text(s, detX, dy); dy += 3.5;
    });

    y = Math.max(imgY + imgH, dy) + 4;

    // Additional images row
    if (images.length > 1) {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFontSize(7); doc.setTextColor(130, 130, 130);
      doc.text('Photos:', 14, y); y += 3;
      const thumbW = 32; const thumbH = 24; const gap = 3;
      let tx = 14;
      for (let ti = 1; ti < Math.min(images.length, 5); ti++) {
        const thumbImg = await loadImage(images[ti]);
        if (thumbImg) {
          doc.addImage(thumbImg, 'JPEG', tx, y, thumbW, thumbH);
        } else {
          doc.setFillColor(240, 240, 240); doc.rect(tx, y, thumbW, thumbH, 'F');
        }
        tx += thumbW + gap;
      }
      y += thumbH + 4;
    }
  }

  // ── Summary ──
  if (y > 240) { doc.addPage(); y = 20; }
  doc.setDrawColor(200, 200, 200); doc.line(14, y, pageW - 14, y); y += 6;
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
  doc.text('SUMMARY', 14, y); y += 6;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
  const vCount = units.filter(u => u._type === 'vehicle').length;
  const eCount = units.filter(u => u._type === 'engine').length;
  const pCount = units.filter(u => u._type === 'part').length;
  const priced = units.filter(u => getFob(u.id)).length;
  const totalFob = units.reduce((s, u) => s + (getFob(u.id)?.price || 0), 0);

  doc.text(`Total units: ${units.length}`, 14, y); y += 4;
  if (vCount) { doc.text(`  Vehicles: ${vCount}`, 14, y); y += 4; }
  if (eCount) { doc.text(`  Engines: ${eCount}`, 14, y); y += 4; }
  if (pCount) { doc.text(`  Spare parts: ${pCount}`, 14, y); y += 4; }
  y += 2;
  doc.text(`Priced: ${priced} units | Total FOB: $${totalFob.toLocaleString()}`, 14, y); y += 4;
  doc.text(`Not priced: ${units.length - priced} units`, 14, y);

  drawFooter(doc, co, pageW);

  const safeName = (customer.name || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
  const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '-');
  doc.save(`Stock_${safeName}_${dateStr}.pdf`);
}
